import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { UserManager, type User } from 'oidc-client-ts';
import { api, ApiError, useApi } from './api/client';
import ErrorBanner from './components/ErrorBanner';
import StartupStatus from './components/StartupStatus';
import { FixBugDialog, FIX_BUG_WORKFLOW_INSTRUCTIONS } from './components/FixBugDialog';
import SettingsIcon from './components/SettingsIcon';
import MarketingLanding from './components/MarketingLanding';
import { TerminalFontSizeProvider } from './components/TerminalFontSizePreference';
import PersonalAssistantDock from './components/personalAssistant/PersonalAssistantDock';
import { AssistantUIProvider } from './components/personalAssistant/AssistantUI';
import InvitationAcceptancePage from './pages/InvitationAcceptancePage';
import ToolBrowserPage from './pages/ToolBrowserPage';
import WorkspaceRoutes from './pages/WorkspaceRoutes';
import GoogleConnectionPage from './pages/GoogleConnectionPage';
import CodexCloudPage from './pages/CodexCloudPage';
import { captureGoogleNavigation } from './utils/googleConnectionNavigation';
import ToolBrowserOriginalWorkspace from './pages/toolBrowser/OriginalWorkspace';
import { browserModeKey } from './pages/toolBrowser/client';
import { reportError } from './utils/errors';
import { authRecovery, mayRecoverUnauthorized, needsInteractiveSession } from './utils/authRecovery';
import { authRedirectNavigator, createAuthLifetime, sharedAuthOperation, type AuthLifetime } from './utils/authLifetime';
import {
  isAuthConfig, isMeResponse, startStartupRequest, startupFailure, startupView, StartupResponseError, STARTUP_TIMEOUT_MS,
  type StartupFailure, type StartupStage
} from './utils/startup';
import type { AppItem, AuthConfig, Conversation, MeResponse, ModelConfig, PlatformLoggerApi } from './types';
import {
  clearPendingInvitationToken,
  INVITATION_REDACTED_PATH,
  invitationTokenFromLocation,
  pendingInvitationReturnPath,
  storePendingInvitationToken
} from './utils/invitationNavigation';
import { isProjectMember } from './utils/projectAccess';
import { authScopes } from './utils/authScopes';
import { authSettings } from './utils/authSettings';
import { preserveOidcGatewayStatus } from './utils/authTransport';
import {
  type AuthReturnState,
  authReturnPathFromLocation,
  pendingAuthReturnPath,
  storeAuthReturnPath,
  takeAuthCallbackReturnPath
} from './utils/authNavigation';

const tokenKey = 'deploy_console_token';
const googleReturnPath = captureGoogleNavigation(window.location.pathname, window.location.search, window.sessionStorage);
if (googleReturnPath) window.history.replaceState(null, '', googleReturnPath);
const aiFenceLinkPendingKey = 'deploy_console_ai_fence_link_pending';
const SILENT_RENEW_URI = `${window.location.origin}/silent-renew.html`;

type NavigationIconName =
  | 'conversations'
  | 'apps'
  | 'resources'
  | 'terminals'
  | 'executors'
  | 'activity'
  | 'jobs'
  | 'logs';

function NavigationIcon({ name }: { name: NavigationIconName }) {
  const paths: Record<NavigationIconName, React.ReactNode> = {
    resources: <><rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9 2v4m6-4v4M9 18v4m6-4v4M2 9h4m-4 6h4m12-6h4m-4 6h4" /><rect x="9" y="9" width="6" height="6" /></>,
    conversations: <><path d="M5 6.5h14v9H9l-4 3v-12Z" /><path d="M9 10h6M9 13h4" /></>,
    apps: <><rect x="4.5" y="4.5" width="6" height="6" /><rect x="13.5" y="4.5" width="6" height="6" /><rect x="4.5" y="13.5" width="6" height="6" /><rect x="13.5" y="13.5" width="6" height="6" /></>,
    terminals: <><rect x="3.5" y="5" width="17" height="14" /><path d="m7 10 3 2.5L7 15M12.5 15H17" /></>,
    executors: <><rect x="4" y="4" width="16" height="6" /><rect x="4" y="14" width="16" height="6" /><path d="M7 7h.01M7 17h.01M11 7h6M11 17h6" /></>,
    activity: <path d="M3 12h4l2-6 4 12 2-6h6" />,
    jobs: <><circle cx="12" cy="12" r="8.5" /><path d="M10 8.5 15.5 12 10 15.5v-7Z" /></>,
    logs: <><path d="M5 6h14M5 12h14M5 18h10" /><circle cx="3" cy="6" r=".5" /><circle cx="3" cy="12" r=".5" /><circle cx="3" cy="18" r=".5" /></>
  };

  return (
    <span className="nav-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">{paths[name]}</svg>
    </span>
  );
}

function resolveRedirectUri() {
  return `${window.location.origin}/`;
}

function resolvePostLogoutRedirectUri() {
  return `${window.location.origin}/logout`;
}

function normalizeIssuer(value?: string | null) {
  return value ? value.replace(/\/+$/, '') : value;
}

function createUserManager(authConfig: AuthConfig, lifetime: AuthLifetime): UserManager | null {
  const settings = authSettings(authConfig, window.location.origin);
  if (!settings) return null;
  const manager = new UserManager({
    ...settings,
    // All renewal goes through our shared, bounded lifetime below. The OIDC
    // service's own timer would otherwise start an unowned parallel renewal.
    automaticSilentRenew: false,
    requestTimeoutInSeconds: STARTUP_TIMEOUT_MS / 1000,
    silentRequestTimeoutInSeconds: STARTUP_TIMEOUT_MS / 1000,
    userStore: lifetime.store
  }, authRedirectNavigator(lifetime));
  preserveOidcGatewayStatus(manager);
  return manager;
}

function AiFenceLinkAuthorizePage({
  apiClient,
  setError
}: {
  apiClient: ReturnType<typeof useApi>;
  setError: (message: string | null) => void;
}) {
  const location = useLocation();
  const linkState = new URLSearchParams(location.search).get('state') ?? '';
  const pending = useRef<{ state: string; request: Promise<{ redirect_url: string }> } | null>(null);
  const [status, setStatus] = useState(
    linkState ? 'Creating AI Fence account link...' : 'Link failed.'
  );

  useEffect(() => {
    let canceled = false;
    if (!linkState) {
      setError('AI Fence link state is missing.');
      return;
    }
    if (!pending.current || pending.current.state !== linkState) {
      pending.current = { state: linkState,
        request: apiClient.post<{ redirect_url: string }>('/api/ai-fence/link/authorize', { state: linkState }) };
    }
    pending.current.request
      .then((response) => {
        if (canceled) return;
        setStatus('Redirecting back to AI Fence...');
        window.location.assign(response.redirect_url);
      })
      .catch((err) => {
        if (canceled) return;
        reportError('Failed to create AI Fence link', err, setError);
        setStatus('Link failed.');
      });
    return () => {
      canceled = true;
    };
  }, [apiClient, linkState, setError]);

  return (
    <div className="card">
      <h2>Connect AI Fence</h2>
      <p>{status}</p>
    </div>
  );
}

function RegularApp() {
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem(tokenKey) ?? '';
    // Old tool-token links wrote the generated 32-byte base64url credential
    // here. Remove that exact legacy format without changing OIDC's existing
    // stale-token recovery behavior for other values.
    if (/^[A-Za-z0-9_-]{43}$/.test(stored)) {
      localStorage.removeItem(tokenKey);
      return '';
    }
    return stored;
  });
  const [profileAttempt, setProfileAttempt] = useState(0);
  const profileGenerationRef = useRef(0);
  const [profileResult, setProfileResult] = useState<{
    token: string; attempt: number; me?: MeResponse; failure?: StartupFailure;
  } | null>(null);
  const currentProfile = profileResult?.token === token && profileResult.attempt === profileAttempt
    ? profileResult : null;
  const me = currentProfile?.me ?? null;
  const [error, setError] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [configAttempt, setConfigAttempt] = useState(0);
  const [configFailure, setConfigFailure] = useState<StartupFailure | null>(null);
  const [sessionFailure, setSessionFailure] = useState<StartupFailure | null>(null);
  const [authConfigLoaded, setAuthConfigLoaded] = useState(false);
  const [authSessionReady, setAuthSessionReady] = useState(false);
  const [authAttempt, setAuthAttempt] = useState(0);
  const authGenerationRef = useRef(0);
  const authLifetimeRef = useRef<AuthLifetime | null>(null);
  const interactiveRequestedRef = useRef(false);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [apps, setApps] = useState<AppItem[]>([]);
  const [fixBugOpen, setFixBugOpen] = useState(false);
  const [fixBugRequestId, setFixBugRequestId] = useState<string>('');
  const renewFnRef = useRef<
    ((reason: string, forceInteractiveLogin?: boolean, failure?: ApiError, signal?: AbortSignal | null) => Promise<string | null>) | null
  >(null);
  const authManagerRef = useRef<UserManager | null>(null);
  const recoverUnauthorized = useCallback((forceInteractiveLogin: boolean, failure: ApiError, signal?: AbortSignal | null, retryToken?: string) => {
    // The retry belongs to this renewal even though accepting its token moved
    // the profile generation. Other old requests cannot stop the new session.
    if (signal?.aborted || !mayRecoverUnauthorized(
      { authGeneration: authAttempt, profileGeneration: profileAttempt, token },
      { authGeneration: authGenerationRef.current, profileGeneration: profileGenerationRef.current,
        token: localStorage.getItem(tokenKey) ?? '' },
      forceInteractiveLogin ? retryToken : undefined
    )) return Promise.resolve(null);
    const renew = renewFnRef.current;
    return renew
      ? renew(
          forceInteractiveLogin ? 'API rejected refreshed token' : 'API request unauthorized',
          forceInteractiveLogin,
          failure,
          signal
        )
      : Promise.resolve(null);
  }, [authAttempt, profileAttempt, token]);
  const apiClient = useApi(token, recoverUnauthorized);
  const location = useLocation();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useLayoutEffect(() => { navigateRef.current = navigate; }, [navigate]);
  const isAiFenceLinkRoute = location.pathname === '/ai-fence/link/authorize';
  const isGoogleConnectionRoute = location.pathname === '/connections/google';
  const isCloudRoute = location.pathname === '/cloud';
  const isInvitationRoute = location.pathname === '/invitations' || location.pathname.startsWith('/invitations/');

  const clearToken = useCallback(() => {
    localStorage.removeItem(tokenKey);
    setToken('');
  }, []);

  const rememberCurrentAuthReturnPath = useCallback(() => {
    const current = authReturnPathFromLocation(
      window.location.pathname,
      window.location.search,
      window.location.hash
    );
    if (current) {
      storeAuthReturnPath(window.sessionStorage, current);
      return current;
    }
    return pendingAuthReturnPath(window.sessionStorage);
  }, []);

  // Keep the invitation token out of the visible URL/history as soon as the
  // SPA starts. A signed-out invitee needs session-scoped storage only to
  // survive the OIDC round trip; signed-in users keep it solely in the page's
  // component state.
  useLayoutEffect(() => {
    if (!isInvitationRoute) {
      return;
    }
    const invitationToken = invitationTokenFromLocation(location.pathname, location.hash);
    if (!invitationToken) {
      return;
    }
    // `me` is deliberately part of this condition: a page reload can restore
    // an OIDC credential before `/api/me` completes, so the invitation page
    // is not mounted yet even though the credential is already non-empty.
    if (me) {
      clearPendingInvitationToken(window.sessionStorage);
    } else {
      storePendingInvitationToken(window.sessionStorage, invitationToken);
    }
    window.history.replaceState(null, '', INVITATION_REDACTED_PATH);
  }, [isInvitationRoute, location.hash, location.pathname, me]);

  const withPlatformLogger = (action: (logger: PlatformLoggerApi) => void) => {
    let attempts = 0;
    let canceled = false;
    const tryApply = () => {
      if (canceled) {
        return;
      }
      const logger = (window as { PlatformLogger?: PlatformLoggerApi }).PlatformLogger;
      if (logger) {
        action(logger);
        return;
      }
      if (attempts < 20) {
        attempts += 1;
        setTimeout(tryApply, 250);
      }
    };
    tryApply();
    return () => {
      canceled = true;
    };
  };

  useEffect(() => {
    return withPlatformLogger((logger) => {
      logger.configure({
        endpoint: `${window.location.origin}/api/logs/frontend`,
        getAuthToken: () => localStorage.getItem(tokenKey) ?? '',
        service: 'mission-control-ui',
        environment: window.location.hostname,
        hookConsole: true,
        consoleLevels: ['error', 'warn']
      });
    });
  }, []);

  useEffect(() => {
    if (!me?.user) {
      return;
    }
    return withPlatformLogger((logger) => {
      logger.setContext({
        userId: me.user.id,
        userEmail: me.user.email ?? undefined,
        userName: me.user.name ?? undefined
      });
    });
  }, [me]);

  useEffect(() => {
    return startStartupRequest(
      async (signal) => {
        const data = await api<unknown>('/auth/config', { signal }, '');
        if (!isAuthConfig(data)) throw new StartupResponseError();
        return data;
      },
      (data) => {
        setAuthConfig(data);
        setConfigFailure(null);
        setAuthConfigLoaded(true);
        if (!data.issuer || !data.client_id) {
          setAuthSessionReady(true);
        }
      },
      (err) => setConfigFailure(startupFailure(err, 'config'))
    );
  }, [configAttempt]);

  useEffect(() => {
    if (!authConfig?.issuer || !authConfig?.client_id) return;
    const issuer = normalizeIssuer(authConfig.issuer) ?? authConfig.issuer;
    const lifetime = createAuthLifetime(window.sessionStorage, `user:${issuer}:${authConfig.client_id}`);
    let manager: UserManager | null;
    try {
      manager = createUserManager(authConfig, lifetime);
    } catch (err) {
      lifetime.end('terminal', true);
      reportError('Invalid login configuration', err, setError);
      setSessionFailure({
        kind: 'configuration',
        title: 'Sign-in configuration needs attention',
        message: 'Ask an administrator to check the browser sign-in configuration, then retry.'
      });
      setAuthSessionReady(true);
      return;
    }
    if (!manager) {
      return;
    }
    authLifetimeRef.current = lifetime;
    authManagerRef.current = manager;
    const logAuthEvent = (label: string, extra?: Record<string, unknown>) => {
      if (extra) {
        console.info(`[auth] ${label}`, extra);
      } else {
        console.info(`[auth] ${label}`);
      }
    };
    const recovery = authRecovery(window.sessionStorage);
    const isCurrent = () => authLifetimeRef.current === lifetime && authGenerationRef.current === authAttempt;
    const active = () => isCurrent() && lifetime.active();
    let renewal: ReturnType<typeof sharedAuthOperation<User | null>> | null = null;
    let interactiveLogin: Promise<void> | null = null;

    const stopRecovery = (
      failure = startupFailure(null, 'session'), clearCredentials = failure.kind !== 'availability'
    ) => {
      if (!isCurrent() || (!lifetime.active() && lifetime.reason() !== 'timeout')) return;
      // Fence pending profile callbacks before React cleans up their effect,
      // including when an availability failure retains the stored token.
      profileGenerationRef.current += 1;
      recovery.block();
      // A gateway outage is not a credential rejection. End this operation
      // and keep the recovery budget blocked, without deleting the stored user.
      lifetime.end('terminal', clearCredentials);
      if (clearCredentials) clearToken();
      setProfileResult(null);
      setSessionFailure(failure);
      setAuthSessionReady(true);
    };
    // Automatic OIDC renewal is disabled. Only completed, owned operations may
    // publish a credential; OIDC's early userLoaded event is not an acceptance.
    const acceptUser = (user: User | null) => {
      if (!active() || recovery.blocked() || !user?.access_token || user.expired) return;
      localStorage.setItem(tokenKey, user.access_token);
      setToken(user.access_token);
      // Even a provider that reuses the token string must get a new access
      // check, and requests from the earlier validation cannot recover it.
      setProfileAttempt(++profileGenerationRef.current);
      setSessionFailure(null);
      setError(null);
    };
    logAuthEvent('auth manager configured', {
      authority: issuer,
      client_id: authConfig.client_id,
      redirect_uri: resolveRedirectUri(),
      post_logout_redirect_uri: resolvePostLogoutRedirectUri(),
      scope: authScopes(authConfig),
      has_offline_access: authScopes(authConfig).split(' ').includes('offline_access'),
      automatic_silent_renew: false,
      silent_redirect_uri: SILENT_RENEW_URI,
      user_store: 'sessionStorage'
    });

    const startInteractiveLogin = async (reason: string) => {
      if (!active()) return;
      if (interactiveLogin) return interactiveLogin;
      if (reason === 'login button') recovery.reset();
      else if (!recovery.claimAutomaticLogin()) { stopRecovery(); return; }
      const returnPath = rememberCurrentAuthReturnPath();
      clearToken();
      setProfileResult(null);
      setSessionFailure(null);
      setAuthSessionReady(false);
      setError(null);
      interactiveLogin = lifetime.run(async () => {
        await manager.removeUser();
        if (!active()) throw new DOMException('Authentication operation cancelled', 'AbortError');
        await manager.signinRedirect({
          state: returnPath ? ({ return_path: returnPath } satisfies AuthReturnState) : undefined
        });
      }).finally(() => {
        interactiveLogin = null;
        if (active()) setAuthSessionReady(true);
      });
      return interactiveLogin;
    };

    const refreshSession = async (
      reason: string, terminal = false, failure?: ApiError, signal?: AbortSignal | null
    ): Promise<string | null> => {
      if (!active() || signal?.aborted) return null;
      if (terminal || recovery.blocked()) {
        stopRecovery(startupFailure(failure, 'session'));
        return null;
      }
      if (renewal?.settled()) renewal = null;
      if (!renewal) {
        if (!recovery.claimSilentRenew()) {
          stopRecovery(startupFailure(failure, 'session'));
          return null;
        }
        renewal = sharedAuthOperation(lifetime, async () => {
          // 3.4.1 overwrites its configured refresh-token timeout with undefined
          // when this argument is omitted. Keep the outer lifetime deadline too.
          const user = await manager.signinSilent({ silentRequestTimeoutInSeconds: STARTUP_TIMEOUT_MS / 1000 });
          if (!user?.access_token || user.expired) throw new StartupResponseError();
          return user;
        }, acceptUser);
      }
      const currentRenewal = renewal;
      try {
        return (await currentRenewal.join(signal))?.access_token ?? null;
      } catch (err) {
        if (!isCurrent() || signal?.aborted ||
            (!lifetime.active() && lifetime.reason() !== 'timeout')) return null;
        if (needsInteractiveSession(err)) {
          try { await startInteractiveLogin(`silent renew failed: ${reason}`); }
          catch (loginError) { stopRecovery(startupFailure(loginError, 'session')); }
        } else {
          stopRecovery(startupFailure(err, 'session'));
        }
        return null;
      } finally {
        if (currentRenewal.settled() && renewal === currentRenewal) renewal = null;
      }
    };
    renewFnRef.current = refreshSession;
    const onTokenExpired = () => { void refreshSession('token expired event'); };
    const onTokenExpiring = () => { void refreshSession('token expiring event'); };
    const onUserUnloaded = () => { if (active()) clearToken(); };
    const onUserSignedOut = () => {
      if (!active()) return;
      rememberCurrentAuthReturnPath();
      authGenerationRef.current += 1;
      lifetime.end('logout', true);
      clearToken();
      setProfileResult(null);
      setSessionFailure(null);
      setAuthSessionReady(true);
    };
    manager.events.addUserUnloaded(onUserUnloaded);
    manager.events.addAccessTokenExpired(onTokenExpired);
    manager.events.addAccessTokenExpiring(onTokenExpiring);
    manager.events.addUserSignedOut(onUserSignedOut);
    lifetime.onEnd(() => {
      manager.stopSilentRenew();
      manager.events.removeUserUnloaded(onUserUnloaded);
      manager.events.removeAccessTokenExpired(onTokenExpired);
      manager.events.removeAccessTokenExpiring(onTokenExpiring);
      manager.events.removeUserSignedOut(onUserSignedOut);
      if (authManagerRef.current === manager) {
        authManagerRef.current = null;
        renewFnRef.current = null;
      }
    });

    if (interactiveRequestedRef.current) {
      interactiveRequestedRef.current = false;
      void startInteractiveLogin('login button').catch(err => stopRecovery(startupFailure(err, 'session')));
    } else if (recovery.blocked()) {
      // Reloading a spent budget supplies no new evidence about credentials.
      // It must not undo retention after an availability failure. Access still
      // remains blocked until the existing explicit sign-in retry succeeds.
      stopRecovery(undefined, false);
    } else {
      const search = window.location.search;
      const callback = search.includes('code=') || search.includes('error=');
      void lifetime.run(() => callback ? manager.signinRedirectCallback() : manager.getUser())
        .then(async user => {
          if (!active()) return;
          if (callback && !user) throw new StartupResponseError();
          if (user?.expired) {
            clearToken();
            await refreshSession('startup expired user');
          } else {
            acceptUser(user);
          }
          if (!active()) return;
          if (callback) {
            const returnPath = takeAuthCallbackReturnPath(window.sessionStorage, user?.state);
            const pendingAiFenceLink = localStorage.getItem(aiFenceLinkPendingKey);
            if (pendingAiFenceLink) {
              localStorage.removeItem(aiFenceLinkPendingKey);
              window.location.replace(pendingAiFenceLink);
              lifetime.end('replaced');
            } else if (pendingInvitationReturnPath(sessionStorage)) {
              navigateRef.current(INVITATION_REDACTED_PATH, { replace: true });
            } else {
              // Chromium Android site-mode changes reload the original URL.
              // A SPA history replacement leaves the authorization redirect
              // there, so switching modes can replay already-consumed state.
              // Commit the saved app destination as a new document instead;
              // its startup still requires a successful /api/me access check.
              window.location.replace(returnPath ?? '/');
              lifetime.end('replaced');
            }
          }
        }).catch(err => {
          if (!isCurrent() || (!lifetime.active() && lifetime.reason() !== 'timeout')) return;
          if (callback) window.history.replaceState(null, '', window.location.pathname);
          stopRecovery(startupFailure(err, 'session'));
        }).finally(() => {
          if (active()) setAuthSessionReady(true);
        });
    }
    return () => lifetime.end('cleanup');
  }, [authConfig, authAttempt, clearToken, rememberCurrentAuthReturnPath]);

  useEffect(() => {
    if (!token || !authConfigLoaded || !authSessionReady || sessionFailure) {
      return;
    }
    const current = () => authGenerationRef.current === authAttempt &&
      profileGenerationRef.current === profileAttempt && localStorage.getItem(tokenKey) === token;
    return startStartupRequest(
      async (signal) => {
        const data = await apiClient.get<unknown>('/api/me', { signal });
        if (!isMeResponse(data)) throw new StartupResponseError();
        return data;
      },
      (data) => {
        if (!current()) return;
        authRecovery(window.sessionStorage).reset();
        setProfileResult({ token, attempt: profileAttempt, me: data });
        setError(null);
      },
      (err) => {
        if (current()) setProfileResult({ token, attempt: profileAttempt, failure: startupFailure(err, 'profile') });
      }
    );
  }, [apiClient, authConfigLoaded, authSessionReady, token, profileAttempt, sessionFailure, authAttempt]);

  // Compute isAdmin early so we can use it in other useEffects
  const roles = me?.roles ?? [];
  const isAdmin = roles.some((role) => {
    const normalized = role.toLowerCase();
    return normalized === 'admin' || normalized === 'apikeyadmin';
  });
  const hasViewSiteRole = roles.some((role) => role.toLowerCase() === 'viewsite');
  const canManageLegacyApps = isAdmin || hasViewSiteRole;

  // Load models and apps for the fix bug dialog (admin only)
  useEffect(() => {
    if (!token || !isAdmin) {
      return;
    }
    apiClient
      .get<ModelConfig[]>('/api/me/models')
      .then((data) => setModels(data))
      .catch((err) => console.error('Failed to load models', err));
    apiClient
      .get<{ apps: AppItem[] }>('/api/apps')
      .then((data) => setApps(data.apps ?? []))
      .catch((err) => console.error('Failed to load apps', err));
  }, [apiClient, token, isAdmin]);

  function handleOpenFixBug(requestId: string) {
    setFixBugRequestId(requestId);
    setFixBugOpen(true);
  }

  function handleFixBugCreated(conversation: Conversation) {
    navigate(`/conversations/${conversation.id}`);
  }

  function logout() {
    rememberCurrentAuthReturnPath();
    authGenerationRef.current += 1;
    authLifetimeRef.current?.end('logout', true);
    clearToken();
    setProfileResult(null);
    setSessionFailure(null);
    setError(null);
    setAuthSessionReady(true);
    window.history.replaceState(null, '', '/logout');
  }

  function login() {
    if (!authConfig?.issuer || !authConfig?.client_id) return;
    authLifetimeRef.current?.end('replaced', true);
    const generation = ++authGenerationRef.current;
    authRecovery(window.sessionStorage).reset();
    clearToken();
    setSessionFailure(null);
    setProfileResult(null);
    setAuthSessionReady(false);
    interactiveRequestedRef.current = true;
    setAuthAttempt(generation);
  }

  function signIn() {
    if (isAiFenceLinkRoute) {
      localStorage.setItem(aiFenceLinkPendingKey, `${location.pathname}${location.search}`);
    }
    login();
  }

  function retryStartup(stage: StartupStage) {
    if (stage === 'config') {
      authLifetimeRef.current?.end('replaced', true);
      setAuthAttempt(++authGenerationRef.current);
      setAuthConfig(null);
      setConfigFailure(null);
      setAuthConfigLoaded(false);
      setAuthSessionReady(false);
      setConfigAttempt(attempt => attempt + 1);
    } else if (stage === 'profile') {
      setProfileResult(null);
      setProfileAttempt(++profileGenerationRef.current);
    } else {
      signIn();
    }
  }

  const startup = startupView({
    configLoaded: authConfigLoaded,
    sessionReady: authSessionReady,
    hasToken: Boolean(token),
    hasUser: Boolean(me),
    loginConfigured: Boolean(authConfig?.issuer && authConfig?.client_id),
    configFailure,
    sessionFailure,
    profileFailure: currentProfile?.failure ?? null
  });
  const canViewExecutorNodes = isAdmin
    || me?.capabilities?.executor_view === true
    || me?.capabilities?.executor_management === true;
  const canMaintainHosts = isAdmin || me?.capabilities?.host_maintenance === true;
  const canViewExecutors = canViewExecutorNodes || canMaintainHosts;
  const canOperateExecutors = isAdmin
    || me?.capabilities?.executor_operate === true
    || me?.capabilities?.executor_management === true;
  const canUseExecutorTerminal = isAdmin || me?.capabilities?.executor_terminal === true;
  const canUsePrivilegedExecutorTerminal = isAdmin
    || me?.capabilities?.executor_terminal_privileged === true;
  const canManageExecutorTerminalIdentities = isAdmin
    || me?.capabilities?.executor_terminal_identity_management === true;
  const canCreate = isAdmin
    || isProjectMember(me)
    || canOperateExecutors
    || canViewExecutors
    || canUseExecutorTerminal
    || hasViewSiteRole;
  const showSidebar = Boolean(token && canCreate);
  const isResourcesRoute = location.pathname.startsWith('/resources');
  const isBackupsRoute = location.pathname === '/backups' || /^\/apps\/[^/]+\/backups$/.test(location.pathname);
  const isSettingsRoute = location.pathname.startsWith('/settings') || isGoogleConnectionRoute;
  const isExecutorsRoute = location.pathname.startsWith('/executors');
  const isTerminalsRoute = location.pathname.startsWith('/terminals');
  const isLogsRoute = location.pathname.startsWith('/logs');
  const isJobsRoute = location.pathname.startsWith('/jobs');
  const isMonitoringRoute = location.pathname.startsWith('/monitoring');
  const isConversationsRoute = location.pathname.includes('/conversations');
  const isPersonalAgentRoute = location.pathname === '/personal-agent' || location.pathname.startsWith('/personal-agent/');
  const isConversationDetailRoute =
    /^\/(?:apps\/[^/]+\/)?conversations\/[^/]+(?:\/|$)/.test(location.pathname);
  const isObservabilityRoute = isLogsRoute || isJobsRoute || isMonitoringRoute;
  const pageTitle = showSidebar
    ? isCloudRoute ? 'Codex Cloud' : isBackupsRoute ? 'Backup archives' : isResourcesRoute ? 'Resources' : isTerminalsRoute
      ? 'Terminals'
      : isExecutorsRoute
      ? 'Executors'
      : isSettingsRoute
      ? 'Settings'
      : isConversationsRoute
        ? 'Conversations'
        : isJobsRoute
        ? 'Jobs'
        : isLogsRoute
          ? 'Log Viewer'
          : isMonitoringRoute
            ? 'Monitoring'
          : 'Apps'
    : 'Mission Control';

  if (!token && !startup) {
    return (
      <MarketingLanding
        canSignIn={Boolean(authConfig?.issuer && authConfig?.client_id)}
        error={error}
        onDismissError={() => setError(null)}
        onSignIn={signIn}
      />
    );
  }

  // App routes are split into page modules under src/app/src/pages.
  return (
    <TerminalFontSizeProvider
      apiClient={apiClient}
      userId={me?.user.id}
      initialFontSize={me?.user.terminal_font_size}
      setError={setError}
    >
    <AssistantUIProvider key={me?.user.id ?? 'signed-out'}>
    <div
      className={[
        'shell',
        showSidebar ? 'shell--with-nav' : '',
        isConversationsRoute ? 'shell--conversation-route' : '',
        isConversationDetailRoute ? 'shell--conversation-detail-route' : '',
        isPersonalAgentRoute ? 'shell--personal-agent-route' : '',
        isTerminalsRoute ? 'shell--terminal-route' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showSidebar && (
        <aside className="sidebar">
          <div className="sidebar-brand">
            <img className="sidebar-brand-logo" src="/brand/yaaf-wordmark.svg" alt="YAAF" />
            <div>
              <strong>Mission Control</strong>
              <span>Agent operations</span>
            </div>
          </div>
          <nav className="sidebar-nav">
            <div className="sidebar-section-label">Main</div>
            <NavLink to="/personal-agent" className={({ isActive }) => (isActive ? 'active' : '')}>
              <NavigationIcon name="conversations" />
              <span>Personal agent</span>
            </NavLink>
            <NavLink to="/conversations" className={({ isActive }) => (isActive ? 'active' : '')}>
              <NavigationIcon name="conversations" />
              <span>Conversations</span>
            </NavLink>
            <NavLink to="/cloud" className={({ isActive }) => (isActive ? 'active' : '')}>
              <NavigationIcon name="conversations" />
              <span>Codex Cloud</span>
            </NavLink>
            <NavLink to="/apps" className={({ isActive }) => (isActive ? 'active' : '')}>
              <NavigationIcon name="apps" />
              <span>Apps</span>
            </NavLink>
            <NavLink to="/resources" className={({ isActive }) => (isActive ? 'active' : '')}>
              <NavigationIcon name="resources" />
              <span>Resources</span>
            </NavLink>
            <NavLink to="/backups" className={({ isActive }) => (isActive ? 'active' : '')}>
              <NavigationIcon name="apps" />
              <span>Backup archives</span>
            </NavLink>
            {canUseExecutorTerminal && (
              <NavLink to="/terminals" className={({ isActive }) => (isActive ? 'active' : '')}>
                <NavigationIcon name="terminals" />
                <span>Terminals</span>
              </NavLink>
            )}
            {canViewExecutors && (
              <NavLink to="/executors" className={({ isActive }) => (isActive ? 'active' : '')}>
                <NavigationIcon name="executors" />
                <span>Executors</span>
              </NavLink>
            )}
            {isAdmin && (
              <>
                <div className="sidebar-divider"></div>
                <div className="sidebar-section-label">Monitoring</div>
                <NavLink to="/monitoring" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <NavigationIcon name="activity" />
                  <span>Activity</span>
                </NavLink>
                <NavLink to="/jobs" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <NavigationIcon name="jobs" />
                  <span>Jobs</span>
                </NavLink>
                <NavLink to="/logs" className={({ isActive }) => (isActive ? 'active' : '')}>
                  <NavigationIcon name="logs" />
                  <span>Logs</span>
                </NavLink>
              </>
            )}
            {isAdmin && (
              <>
                <div className="sidebar-divider"></div>
                <div className="sidebar-section-label">System</div>
                <NavLink to="/settings" className={({ isActive }) => (isActive || isGoogleConnectionRoute ? 'active' : '')}>
                  <span className="icon">
                    <SettingsIcon />
                  </span>
                  <span>Settings</span>
                </NavLink>
              </>
            )}
          </nav>
          <div className="sidebar-footer">
            {me && (
              <div className="sidebar-user">
                <div className="sidebar-avatar">
                  {(me.user.name ?? me.user.email ?? 'A').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <strong>{me.user.name ?? me.user.email ?? 'Admin'}</strong>
                  <span>{me.roles.join(', ') || 'none'}</span>
                </div>
              </div>
            )}
            {token && (
              <button onClick={logout} className="ghost">
                Logout
              </button>
            )}
          </div>
        </aside>
      )}

      <div className="app">
        {!showSidebar && (
          <header>
            <div>
              <h1>Mission Control</h1>
              <p>Agent workspace with role-based access.</p>
            </div>
            {token && (
              <div className="auth">
                <button onClick={logout}>Logout</button>
              </div>
            )}
          </header>
        )}

        {showSidebar && !isPersonalAgentRoute && (
          <header className="topbar">
            <div className="page-title">
              <span className="page-kicker">
                {isResourcesRoute ? 'Coordination' : isSettingsRoute ? 'Configuration' : isObservabilityRoute ? 'Observability' : isExecutorsRoute ? 'Infrastructure' : 'Workspace'}
              </span>
              <h1>{pageTitle}</h1>
            </div>
          </header>
        )}

        <main className="content">
          {startup && <StartupStatus state={startup} onRetry={retryStartup} />}
          {error && (
            <ErrorBanner
              message={error}
              onDismiss={() => setError(null)}
              isAdmin={isAdmin}
              onFixBug={
                error.match(/\(request ([^)]+)\)/i)
                  ? () => {
                      const match = error.match(/\(request ([^)]+)\)/i);
                      if (match) handleOpenFixBug(match[1]);
                    }
                  : undefined
              }
            />
          )}

          {token && me && isGoogleConnectionRoute && <GoogleConnectionPage apiClient={apiClient} />}
          {token && me && isCloudRoute && <CodexCloudPage apiClient={apiClient} />}

          {token && me && isAiFenceLinkRoute && (
            <Routes>
              <Route
                path="/ai-fence/link/authorize"
                element={<AiFenceLinkAuthorizePage apiClient={apiClient} setError={setError} />}
              />
            </Routes>
          )}

          {token && me && !canCreate && !isAiFenceLinkRoute && !isGoogleConnectionRoute && !isCloudRoute && !(location.pathname === '/invitations' || location.pathname.startsWith('/invitations/')) && (
            <div className="card">
              <h2>Thanks for signing in</h2>
              <p>You are on the waitlist. We will reach out once access is enabled.</p>
              <button onClick={logout}>Logout</button>
            </div>
          )}

          {token && me && !isAiFenceLinkRoute && (location.pathname === '/invitations' || location.pathname.startsWith('/invitations/')) && (
            <Routes>
              <Route path="/invitations" element={<InvitationAcceptancePage apiClient={apiClient} setError={setError} />} />
              <Route path="/invitations/:token" element={<InvitationAcceptancePage apiClient={apiClient} setError={setError} />} />
            </Routes>
          )}

          {token && canCreate && !isAiFenceLinkRoute && !isGoogleConnectionRoute && !isCloudRoute && !(location.pathname === '/invitations' || location.pathname.startsWith('/invitations/')) && (
            <WorkspaceRoutes
              apiClient={apiClient} setError={setError} isAdmin={isAdmin}
              canManageApps={canManageLegacyApps} canCreate={canCreate}
              canViewExecutors={canViewExecutors} canOperateExecutors={canOperateExecutors}
              canViewExecutorNodes={canViewExecutorNodes} canMaintainHosts={canMaintainHosts}
              canUseExecutorTerminal={canUseExecutorTerminal}
              canUsePrivilegedExecutorTerminal={canUsePrivilegedExecutorTerminal}
              canManageExecutorTerminalIdentities={canManageExecutorTerminalIdentities}
            />
          )}

          <FixBugDialog
            open={fixBugOpen}
            onClose={() => setFixBugOpen(false)}
            apiClient={apiClient}
            models={models}
            apps={apps}
            defaultAppId={apps.find((app) => app.is_self)?.id}
            defaultTitle="Fix Bug"
            defaultPrompt={`Please investigate and fix the issue shown in the logs.

You can use the MCP tools to query logs:
- Use get_logs with the request_id "${fixBugRequestId}" to investigate
- Use get_log_detail to get full details of specific log entries

Focus on understanding what went wrong from the error messages, relevant traces and spans, and the root cause.

${FIX_BUG_WORKFLOW_INSTRUCTIONS}`}
            onCreated={handleFixBugCreated}
            setError={setError}
          />
        </main>
      </div>
      {token && me && canCreate && <PersonalAssistantDock key={me.user.id} ownerId={me.user.id} apiClient={apiClient} />}
    </div>
    </AssistantUIProvider>
    </TerminalFontSizeProvider>
  );
}

export default function App() {
  const location = useLocation();
  if (location.pathname === '/login/tool-token' || location.pathname === '/tool-browser') {
    return <ToolBrowserPage />;
  }
  if (sessionStorage.getItem(browserModeKey) === 'scoped') {
    return <ToolBrowserOriginalWorkspace />;
  }
  return <RegularApp />;
}
